import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CustomerCombobox, ProductCombobox } from "@/components/InvoiceCombos";
import { GstBreakdown } from "@/components/GstBreakdown";
import { useDayBookReturn } from "@/lib/useDayBookReturn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditInvoice() {
  const returnTo = useDayBookReturn();
  const { id } = useParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({ customer_id: "", invoice_no: "", invoice_date: "", due_date: "", gst_type: "cgst_sgst", rcm: false, notes: "", delivery_note: "", vehicle_no: "", eway_bill_no: "", ship_to_name: "", ship_to_address: "", ship_to_city: "", ship_to_state: "", ship_to_pincode: "", ship_to_gstin: "", show_payment_status: null });
  const [shipToOpen, setShipToOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sellerStateCode, setSellerStateCode] = useState("");

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      const [invoiceRes, customersRes, productsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/invoices/${id}`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/products`),
        axios.get(`${API}/settings`).catch(() => ({ data: {} })),
      ]);
      const inv = invoiceRes.data;
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      const s = settingsRes.data || {};
      setSellerStateCode((s.state_code || (s.gstin || "").slice(0, 2) || "").toString());
      setFormData({ customer_id: inv.customer_id, invoice_no: inv.invoice_no, invoice_date: inv.invoice_date, due_date: inv.due_date || "", gst_type: inv.gst_type || "cgst_sgst", rcm: inv.rcm || false, notes: inv.notes || "", delivery_note: inv.delivery_note || "", vehicle_no: inv.vehicle_no || "", eway_bill_no: inv.eway_bill_no || "", ship_to_name: inv.ship_to_name || "", ship_to_address: inv.ship_to_address || "", ship_to_city: inv.ship_to_city || "", ship_to_state: inv.ship_to_state || "", ship_to_pincode: inv.ship_to_pincode || "", ship_to_gstin: inv.ship_to_gstin || "", show_payment_status: inv.show_payment_status });
      setShipToOpen(!!(inv.ship_to_name || inv.ship_to_address || inv.ship_to_city));
      // Hydrate `amount_overridden` from the persisted flag; fall back to
      // detecting an override by comparing the stored amount against
      // qty×price (for legacy invoices saved before the flag was added).
      setItems((inv.items || []).map((it) => {
        const expected = (parseFloat(it.quantity) || 0) * (parseFloat(it.price) || 0);
        const flag = typeof it.amount_overridden === "boolean"
          ? it.amount_overridden
          : Math.abs((parseFloat(it.amount) || 0) - expected) > 0.01;
        return { ...it, amount_overridden: flag };
      }));
      setSelectedCustomer(customersRes.data.find((c) => c.id === inv.customer_id));
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    const a = (sellerStateCode || "").trim().padStart(2, "0").slice(0, 2);
    const b = (customer.state_code || "").trim().padStart(2, "0").slice(0, 2);
    const derived = (a && b && a !== "00" && b !== "00") ? (a === b ? "cgst_sgst" : "igst") : null;
    setFormData((prev) => ({
      ...prev,
      customer_id: customer.id,
      // preserve user's explicit Tax-Free (no_gst) choice
      gst_type: prev.gst_type === "no_gst" ? "no_gst" : (derived || prev.gst_type),
    }));
  };

  const handleProductSelect = (index, product) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      product_id: product.id,
      product_name: product.name,
      hsn_sac: product.hsn_sac,
      price: product.price,
      gst_rate: product.gst_rate,
      unit: product.unit || "PCS",
      amount_overridden: false,
    };
    newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(product.price) || 0);
    setItems(newItems);
  };

  const addItem = () => setItems([...items, { product_id: "", product_name: "", hsn_sac: "", quantity: 1, price: 0, gst_rate: 18, amount: 0 }]);
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === "product_id") {
      const product = products.find((p) => p.id === value);
      if (product) {
        newItems[index].product_name = product.name; newItems[index].hsn_sac = product.hsn_sac;
        newItems[index].price = product.price; newItems[index].gst_rate = product.gst_rate;
        newItems[index].unit = product.unit || "PCS";
        newItems[index].amount_overridden = false;
      }
    }
    if (field === "amount") {
      newItems[index].amount = parseFloat(value) || 0;
      newItems[index].amount_overridden = true;
    } else if ((field === "quantity" || field === "price") && !newItems[index].amount_overridden) {
      newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].price) || 0);
    }
    setItems(newItems);
  };

  const resetAmount = (index) => {
    const newItems = [...items];
    newItems[index].amount_overridden = false;
    newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].price) || 0);
    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((s, i) => s + i.amount, 0);
    const totalGst = items.reduce((s, i) => s + (i.amount * i.gst_rate) / 100, 0);
    let cgst = 0, sgst = 0, igst = 0;
    if (formData.gst_type === "cgst_sgst") { cgst = totalGst / 2; sgst = totalGst / 2; } else { igst = totalGst; }
    return { subtotal, cgst, sgst, igst, total: formData.rcm ? subtotal : subtotal + cgst + sgst + igst };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || items.length === 0) { alert("Please fill all required fields"); return; }
    if (items.some((it) => !it.product_id)) { alert("Please select a product for every item"); return; }
    try {
      await axios.put(`${API}/invoices/${id}`, { ...formData, items });
      navigate(returnTo || `/invoices/${id}`);
    } catch (error) {
      console.error("Error updating invoice:", error);
      alert("Failed to update invoice.");
    }
  };

  const totals = calculateTotals();

  if (loading) return <div className="p-8"><div className="animate-pulse"><div className="h-96 bg-slate-200 rounded"></div></div></div>;

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="edit-invoice-page">
      <div className="mb-8">
        <button onClick={() => navigate(`/invoices/${id}`)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors duration-200">
          <ArrowLeft size={20} /><span className="text-sm font-semibold">Back to Invoice</span>
        </button>
        <h1 className="text-4xl font-black tracking-tighter text-slate-900 mb-2">Edit Invoice</h1>
        <p className="text-slate-500 text-sm">{formData.invoice_no}</p>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Invoice Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Invoice Number *</Label><Input data-testid="edit-invoice-no" value={formData.invoice_no} onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })} required /></div>
                <div><Label>Invoice Date *</Label><Input type="date" data-testid="edit-invoice-date" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} required /></div>
                <div><Label>Motor Vehicle No</Label><Input data-testid="edit-vehicle-no-top" value={formData.vehicle_no} onChange={(e) => setFormData({ ...formData, vehicle_no: e.target.value })} placeholder="e.g. MH 01 AB 1234" /></div>
                <div><Label>Due Date</Label><Input type="date" data-testid="edit-due-date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} /></div>
                <div>
                  <Label>Customer *</Label>
                  <CustomerCombobox
                    customers={customers}
                    value={formData.customer_id}
                    onSelect={handleCustomerSelect}
                    onCreated={(c) => setCustomers((prev) => [...prev, c])}
                    testId="edit-customer-select"
                  />
                </div>
                <div className="col-span-2">
                  <Label>GST Type *</Label>
                  <select data-testid="edit-gst-type" value={formData.gst_type} onChange={(e) => setFormData({ ...formData, gst_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold" required>
                    <option value="cgst_sgst">CGST + SGST (Intra-State)</option>
                    <option value="igst">IGST (Inter-State)</option>
                    <option value="no_gst">No GST / Nil Rated (Tax-Free Bill)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm bg-slate-50">
                    <div>
                      <p className="text-sm font-bold text-slate-900">Reverse Charge Mechanism (RCM)</p>
                      <p className="text-xs text-slate-500">Tax payable by the buyer/recipient — invoice total = taxable amount</p>
                    </div>
                    <Switch checked={formData.rcm} onCheckedChange={(v) => setFormData({ ...formData, rcm: v })} data-testid="edit-rcm-toggle" />
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-900">Items</h2>
                <button type="button" onClick={addItem} data-testid="edit-add-item-btn" className="px-3 py-2 bg-blue-600 text-white rounded-sm text-sm font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center gap-2"><Plus size={16} weight="bold" /> Add Item</button>
              </div>
              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} data-testid={`edit-item-${index}`} className="border border-slate-200 rounded-sm p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 grid grid-cols-5 gap-3">
                        <div className="col-span-2">
                          <Label>Product *</Label>
                          <ProductCombobox
                            products={products}
                            value={item.product_id}
                            onSelect={(p) => handleProductSelect(index, p)}
                            onCreated={(p) => setProducts((prev) => [...prev, p])}
                            testId={`edit-item-product-select-${index}`}
                          />
                          {(() => {
                            const p = products.find((x) => x.id === item.product_id);
                            if (!p) return null;
                            const stock = p.current_stock ?? 0;
                            const reorder = p.reorder_level ?? 0;
                            const afterSale = stock - (parseFloat(item.quantity) || 0);
                            let cls = "bg-emerald-100 text-emerald-700";
                            let icon = "●";
                            if (afterSale < 0) { cls = "bg-red-100 text-red-700"; icon = "⚠"; }
                            else if (stock <= reorder && reorder > 0) { cls = "bg-amber-100 text-amber-800"; icon = "▲"; }
                            return (
                              <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${cls}`}>
                                <span>{icon}</span>
                                <span>Available: {stock} {p.unit || "PCS"}</span>
                                {afterSale < 0 && (
                                  <span className="ml-1 font-bold uppercase">Will go negative ({afterSale.toFixed(2)})</span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div><Label>Qty *</Label><Input type="number" step="0.01" min="0.01" data-testid={`edit-item-quantity-${index}`} value={item.quantity} onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)} required /></div>
                        <div><Label>Price</Label><Input type="number" step="0.01" data-testid={`edit-item-price-${index}`} value={item.price} onChange={(e) => updateItem(index, "price", parseFloat(e.target.value) || 0)} required /></div>
                        <div>
                          <div className="flex items-center justify-between gap-1">
                            <Label className="flex items-center gap-1.5">
                              Amount
                              {item.amount_overridden && (
                                <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700" title="Manually overridden — auto-calc disabled">Overridden</span>
                              )}
                            </Label>
                            {item.amount_overridden && (
                              <button
                                type="button"
                                onClick={() => resetAmount(index)}
                                data-testid={`edit-item-amount-reset-${index}`}
                                className="text-[10px] text-slate-500 hover:text-slate-900 underline"
                                title="Restore auto-calculated amount (qty × price)"
                              >Reset</button>
                            )}
                          </div>
                          <Input
                            type="number" step="0.01" min="0"
                            data-testid={`edit-item-amount-${index}`}
                            value={item.amount}
                            onChange={(e) => updateItem(index, "amount", e.target.value)}
                            className={item.amount_overridden ? "border-amber-400 bg-amber-50/40" : ""}
                          />
                        </div>
                      </div>
                      <button type="button" onClick={() => removeItem(index)} className="mt-6 p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm"><Trash size={18} /></button>
                    </div>
                    {item.hsn_sac && <div className="mt-2 flex gap-4 text-xs text-slate-500"><span>HSN/SAC: {item.hsn_sac}</span><span>GST: {item.gst_rate}%</span></div>}
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-1">Transport & Delivery</h2>
              <p className="text-xs text-slate-500 mb-4">Optional fields shown on invoice PDF</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div><Label>Delivery Note</Label><Input data-testid="edit-delivery-note" autoCap value={formData.delivery_note} onChange={(e) => setFormData({ ...formData, delivery_note: e.target.value })} placeholder="e.g. DN-2026-001" /></div>
                <div><Label>Motor Vehicle No</Label><Input data-testid="edit-vehicle-no" value={formData.vehicle_no} onChange={(e) => setFormData({ ...formData, vehicle_no: e.target.value })} placeholder="e.g. MH 01 AB 1234" /></div>
                <div><Label>E-Way Bill No</Label><Input data-testid="edit-eway-bill" value={formData.eway_bill_no} onChange={(e) => setFormData({ ...formData, eway_bill_no: e.target.value })} placeholder="e.g. 123456789012" /></div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-xl font-bold text-slate-900">Ship To (Optional)</h2>
                <button type="button" onClick={() => setShipToOpen(v => !v)} data-testid="edit-ship-to-toggle" className="text-xs text-blue-600 font-semibold hover:underline">{shipToOpen ? "Hide" : "Add shipping address"}</button>
              </div>
              <p className="text-xs text-slate-500 mb-4">Leave empty for a single-column layout on PDF.</p>
              {shipToOpen && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Recipient Name</Label><Input data-testid="edit-ship-to-name" autoCap value={formData.ship_to_name} onChange={(e) => setFormData({...formData, ship_to_name: e.target.value})} /></div>
                  <div><Label>GSTIN</Label><Input data-testid="edit-ship-to-gstin" value={formData.ship_to_gstin} onChange={(e) => setFormData({...formData, ship_to_gstin: e.target.value.toUpperCase()})} /></div>
                  <div className="sm:col-span-2"><Label>Address</Label><Input data-testid="edit-ship-to-address" autoCap value={formData.ship_to_address} onChange={(e) => setFormData({...formData, ship_to_address: e.target.value})} /></div>
                  <div><Label>City</Label><Input data-testid="edit-ship-to-city" autoCap value={formData.ship_to_city} onChange={(e) => setFormData({...formData, ship_to_city: e.target.value})} /></div>
                  <div><Label>State</Label><Input data-testid="edit-ship-to-state" autoCap value={formData.ship_to_state} onChange={(e) => setFormData({...formData, ship_to_state: e.target.value})} /></div>
                  <div><Label>Pincode</Label><Input data-testid="edit-ship-to-pincode" value={formData.ship_to_pincode} onChange={(e) => setFormData({...formData, ship_to_pincode: e.target.value})} /></div>
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Notes</h2>
              <Textarea data-testid="edit-notes" autoCap value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} />
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-sm p-6 sticky top-8">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Summary</h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm"><span className="text-slate-600">Subtotal</span><span className="font-semibold">{"\u20b9"}{totals.subtotal.toFixed(2)}</span></div>
                {formData.gst_type === "no_gst" ? (<div className="flex justify-between text-sm"><span className="text-slate-500 italic">Tax</span><span className="font-semibold text-slate-500 italic">Not applicable</span></div>) : (<GstBreakdown items={items} gstType={formData.gst_type} />)}
                {formData.rcm && formData.gst_type !== "no_gst" && (<div className="flex justify-between text-xs bg-red-50 border border-red-200 rounded-sm p-2" data-testid="edit-rcm-info-line"><span className="text-red-700 font-semibold">Tax payable by recipient (RCM)</span><span className="font-bold text-red-700">{"\u20b9"}{(totals.cgst + totals.sgst + totals.igst).toFixed(2)}</span></div>)}
                <div className="pt-3 border-t border-slate-200"><div className="flex justify-between"><span className="text-lg font-bold">{formData.rcm ? "Total (Taxable)" : "Total"}</span><span className="text-2xl font-black">{"\u20b9"}{totals.total.toFixed(2)}</span></div></div>
              </div>
              <div className="space-y-3">
                <Button type="submit" data-testid="update-invoice-btn" className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold">Update Invoice</Button>
                <Button type="button" variant="outline" onClick={() => navigate(`/invoices/${id}`)} className="w-full">Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
